import { AxiosResponse } from "axios";
import { apiInstance } from "../base";
import { BookFlightRequest, IAddOpsResponse, IAircraft, IAirline, IAirport, IBookingPaymentResponse, IBonusBalanceResponse, ICountry, IGetAircraftRequest, IGetAirlinesRequest, IGetAirportsRequest, IGetCountriesRequest, IGetTicketsResponse, IListBookingsResponse, ISavedDocument, ISearchFlightsRequest, ISearchFlightsResponse } from "./types";

const BALANCE_REQUEST_INTERVAL_MS = 3000
let balanceRequestPromise: Promise<AxiosResponse<IBonusBalanceResponse>> | null = null
let lastBalanceRequestStartedAt = 0

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const searchFlights = (request: ISearchFlightsRequest): Promise<AxiosResponse<ISearchFlightsResponse>> => {
    return apiInstance.handler.post<ISearchFlightsResponse>(`/aviabooking/flights`, request)
}

export const getAdditionalOptions = (flightId: string): Promise<AxiosResponse<IAddOpsResponse>> => {
    return apiInstance.handler.get<IAddOpsResponse>(`/aviabooking/flights/${flightId}/add-ops`)
}

export const getCountries = (request: IGetCountriesRequest): Promise<AxiosResponse<ICountry[]>> => {
    return apiInstance.handler.get<ICountry[]>(`/aviabooking/countries`, {
        params: request
    })
}

export const getAirports = (request: IGetAirportsRequest): Promise<AxiosResponse<IAirport[]>> => {
    return apiInstance.handler.get<IAirport[]>(`/aviabooking/airports`, {
        params: request
    })
}

export const getAirlines = (request: IGetAirlinesRequest): Promise<AxiosResponse<IAirline[]>> => {
    return apiInstance.handler.get<IAirline[]>(`/aviabooking/airlines`, {
        params: request
    })
}

export const getAircraft = (request: IGetAircraftRequest): Promise<AxiosResponse<IAircraft[]>> => {
    return apiInstance.handler.get<IAircraft[]>(`/aviabooking/aircraft`, {
        params: request
    })
}

export const bookFlight = (flightId: string, request: BookFlightRequest): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post<void>(`/aviabooking/flights/${flightId}/book`, request)
}

export const getSavedDocuments = (): Promise<AxiosResponse<ISavedDocument[]>> => {
    return apiInstance.handler.get<ISavedDocument[]>(`/aviabooking/saved-documents`)
}

export const deleteSavedDocument = (documentId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete<void>(`/aviabooking/saved-documents/${documentId}`)
}

export const listBookings = (page: number, limit: number): Promise<AxiosResponse<IListBookingsResponse>> => {
    return apiInstance.handler.get<IListBookingsResponse>(`/aviabooking/bookings`, {
        params: {
            page,
            limit
        }
    })
}

export const buildCancelBookingRequestBody = (reason?: string): { reason: string } | undefined => {
    const trimmedReason = reason?.trim()
    return trimmedReason ? { reason: trimmedReason } : undefined
}

export const cancelBooking = (bookingId: string, reason?: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post<void>(
        `/aviabooking/bookings/${bookingId}/cancel`,
        buildCancelBookingRequestBody(reason)
    )
}

export const acknowledgePriceChange = (bookingId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post<void>(`/aviabooking/bookings/${bookingId}/acknowledge-price-change`)
}

export const bookingPayment = (bookingId: string): Promise<AxiosResponse<IBookingPaymentResponse>> => {
    return apiInstance.handler.post<IBookingPaymentResponse>(`/aviabooking/bookings/${bookingId}/payment`)
}

export const getBonusBalance = (): Promise<AxiosResponse<IBonusBalanceResponse>> => {
    if (balanceRequestPromise) {
        return balanceRequestPromise
    }

    balanceRequestPromise = (async () => {
        const elapsed = Date.now() - lastBalanceRequestStartedAt
        if (elapsed < BALANCE_REQUEST_INTERVAL_MS) {
            await delay(BALANCE_REQUEST_INTERVAL_MS - elapsed)
        }

        lastBalanceRequestStartedAt = Date.now()
        return apiInstance.handler.get<IBonusBalanceResponse>(`/aviabooking/book/balance`)
    })().finally(() => {
        balanceRequestPromise = null
    })

    return balanceRequestPromise
}

export const getTickets = (bookingId: string): Promise<AxiosResponse<IGetTicketsResponse>> => {
    return apiInstance.handler.get<IGetTicketsResponse>(`/aviabooking/bookings/${bookingId}/tickets`)
}